import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import { useCompanyFeatures } from '../hooks/useCompanyFeatures'

export interface CompanyInfo {
  id: string
  name: string
  logoUrl?: string
}

interface CompanyContextValue {
  selectedCompanyId: string | null
  selectedCompany: CompanyInfo | null
  setSelectedCompany: (companyId: string | null) => void
  accessibleCompanies: CompanyInfo[]
  multiCompanyEnabled: boolean
  effectiveCompanyIds: string[]
  loading: boolean
  refetch: () => Promise<void>
}

const CompanyContext = createContext<CompanyContextValue | null>(null)

export const useCompanyContext = () => {
  const ctx = useContext(CompanyContext)
  if (!ctx) {
    return {
      selectedCompanyId: null,
      selectedCompany: null,
      setSelectedCompany: () => {},
      accessibleCompanies: [],
      multiCompanyEnabled: false,
      effectiveCompanyIds: [],
      loading: false,
      refetch: async () => {}
    }
  }
  return ctx
}

interface CompanyProviderProps {
  children: React.ReactNode
  user: { company?: CompanyInfo; accessibleCompanies?: CompanyInfo[] } | null
  isAuthenticated: boolean
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({
  children,
  user,
  isAuthenticated
}) => {
  const {
    multiCompanyEnabled,
    linkedCompanyIds,
    effectiveCompanyIds,
    loading,
    refetch
  } = useCompanyFeatures(isAuthenticated)

  const accessibleCompanies = useMemo(() => {
    const fromUser = user?.accessibleCompanies ?? (user?.company ? [user.company] : [])
    return fromUser
  }, [user])

  const [selectedCompanyId, setSelectedCompanyIdState] = useState<string | null>(() => {
    return user?.company?.id ?? null
  })

  const setSelectedCompany = useCallback((companyId: string | null) => {
    setSelectedCompanyIdState(companyId)
  }, [])

  const selectedCompany = useMemo(() => {
    if (!selectedCompanyId) return user?.company ?? null
    return (
      accessibleCompanies.find((c) => c.id === selectedCompanyId) ??
      user?.company ??
      null
    )
  }, [selectedCompanyId, accessibleCompanies, user?.company])

  const effectiveIds = useMemo(() => {
    if (multiCompanyEnabled && linkedCompanyIds.length > 0) {
      return linkedCompanyIds
    }
    if (selectedCompanyId) {
      return [selectedCompanyId]
    }
    return user?.company?.id ? [user.company.id] : effectiveCompanyIds
  }, [multiCompanyEnabled, linkedCompanyIds, selectedCompanyId, user?.company?.id, effectiveCompanyIds])

  const value: CompanyContextValue = useMemo(
    () => ({
      selectedCompanyId,
      selectedCompany: selectedCompany ?? null,
      setSelectedCompany,
      accessibleCompanies,
      multiCompanyEnabled,
      effectiveCompanyIds: effectiveIds,
      loading,
      refetch
    }),
    [
      selectedCompanyId,
      selectedCompany,
      setSelectedCompany,
      accessibleCompanies,
      multiCompanyEnabled,
      effectiveIds,
      loading,
      refetch
    ]
  )

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  )
}
